import React from "react";
import { get } from "lodash";
import { BaseAction, EventTrackingType } from "@al/aku-lc-core";
import { CtxType, FormattedMessage } from "@al/next/al-ex";
import { Toast } from "antd-mobile";

import { ChannelType, ShareInfo } from "~/components/basic/Share/types";
import _share from "~/components/basic/Share/_share";

import analyzeLink from "../utils/analyzeLink";
import { SharePanelProps } from "../components/SharePanel";
import { ShareContentsMap, ShareChannelName, ShareChannelNameToID, ShareChannelIdToName } from "../model/shares";

import { NeedBeforeExecuteActionOptions } from "./interfaces";

export type ShareActionPayload = {
  ctx: CtxType;
  onChangeSharePanelProps: (params: SharePanelProps) => void;
  shareContentsMap: ShareContentsMap;
  shareUrl: string;
  sharePanelTitle?: string;
  sharePanelEnableQrCode: boolean;
  sharePanelQrCodeBg?: string;
  onShareChanelClick?: (eventType: EventTrackingType, data?: any) => void;
};

class ShareAction extends BaseAction<ShareActionPayload> {
  onBeforeExecute?: () => boolean | void;

  constructor(options: NeedBeforeExecuteActionOptions<ShareActionPayload>) {
    super(options);
    this.onBeforeExecute = options.onBeforeExecute;
  }

  // 实现 execute 方法
  execute = async (...rest: any[]): Promise<void> => {
    if (this.onBeforeExecute?.() === false) {
      return;
    }
    const shareChannel: ShareChannelName = get(rest[rest.length - 1], "type");
    const channelId = ShareChannelNameToID[shareChannel];
    if (shareChannel && channelId) {
      this.share(channelId);
    } else {
      this.payload.onChangeSharePanelProps?.({
        visible: true,
        title: this.payload.sharePanelTitle,
        getShareInfo: this.getShareInfo,
        enableQrCode: this.payload.sharePanelEnableQrCode,
        qrCodeBg: this.payload.sharePanelQrCodeBg,
        onClose: () => {
          this.payload.onChangeSharePanelProps({
            title: this.payload.sharePanelTitle,
            visible: false,
          });
        },
      });
    }
  };

  // 分享配置
  getShareInfo = async (channelId: ChannelType): Promise<ShareInfo & { callback?: () => void }> => {
    const shareTime = Date.now();
    const { shareContentsMap, shareUrl } = this.payload;
    const ChannelTypeKey = ChannelType[channelId] as keyof typeof ShareChannelName;
    const shareConfig = shareContentsMap[ShareChannelName[ChannelTypeKey]];
    const uid = get(this.payload.ctx.store.getState(), "sign.userDetail.uid");
    const url = analyzeLink(shareUrl);
    const shareInfo = {
      shareContentUrl: url
        ? `${url}${uid ? `&code=${uid}` : ""}&channelId=${channelId}&shareTime=${shareTime}`
        : undefined,
      shareContentTitle: shareConfig.title,
      shareImageUrl: shareConfig.img,
      shareContentDescription: shareConfig.description,
      callback: (data: any) => {
        // 点击面板具体渠道回调
        // success 分享成功失败，callbackType 1 是点击分享按钮触发的，2 是分享成功返回调用的
        if (data?.data?.callbackType === 1) {
          this.payload.onShareChanelClick?.("click", {
            sa: {
              element_name: "click_share",
              // 分享按钮组件类型固定为 3，其他参考埋点文档。
              componentType: 3,
              channel: ShareChannelIdToName[channelId] ?? "",
            },
          });
        }
      },
      // sharePageId: SaReportStaticData.home.pageInfo.pageId,
      // sharePageName: SaReportStaticData.home.pageInfo.pageName,
    } as ShareInfo & { callback?: () => void };
    if (channelId === ChannelType.CopyLink) {
      Toast.info(<FormattedMessage keys={`global:复制成功`} />);
    }
    return shareInfo;
  };

  share = async (channelId: ChannelType): Promise<void> => {
    const shareInfo = await this.getShareInfo(channelId);
    // 如果开启了二维码分享就可以获取到离屏渲染的 canvas
    if (channelId === ChannelType.Whatsapp) {
      const canvasEl = document.querySelector<HTMLCanvasElement>(".off_screen_canvas");
      canvasEl?.dataset?.remoteUrl && Object.assign(shareInfo, { shareImageUrl: canvasEl.dataset.remoteUrl });
    }
    await _share(shareInfo, channelId);
  };
}

export default ShareAction;
